import { connect } from 'react-redux';
import { isMobileBrowser } from '../../../base/environment/utils';
import { translate } from '../../../base/i18n/functions';
import { IconUsers } from '../../../base/icons/svg';
import { getParticipantCountForDisplay } from '../../../base/participants/functions';
import AbstractButton from '../../../base/toolbox/components/AbstractButton';
import { closeOverflowMenuIfOpen } from '../../../toolbox/actions.web';
import { isParticipantsPaneEnabled } from '../../functions';
import { SidePanelTabId } from '../../../side-panel/constants';
import { toggleSidePanelApp } from '../../../side-panel/actions';
/**
 * Implementation of a button for accessing participants pane.
 *
 * 底栏文案直接输出「成员(n)」，不依赖 ToolboxItem 二次 t() + labelProps 插值
 *（缺参时 i18next 会把 {{participantCount}} 替成空串，显示成「成员()」）。
 * 人数只出现在括号内，不再渲染右上角角标。
 */
class ParticipantsPaneButton extends AbstractButton {
    toggledAccessibilityLabel = 'toolbar.accessibilityLabel.closeParticipantsPane';
    icon = IconUsers;
    label = 'toolbar.participants';
    tooltip = 'toolbar.participants';
    toggledTooltip = 'toolbar.closeParticipantsPane';
    /**
     * Indicates whether this button is in toggled state or not.
     *
     * @override
     * @protected
     * @returns {boolean}
     */
    _isToggled() {
        return this.props._isOpen;
    }
    /**
     * Handles clicking the button, and toggles the participants pane.
     *
     * @private
     * @returns {void}
     */
    _handleClick() {
        const { dispatch } = this.props;
        dispatch(closeOverflowMenuIfOpen());
        dispatch(toggleSidePanelApp(SidePanelTabId.PARTICIPANTS));
    }
    /**
     * 已插值的「成员(n)」文案；ToolboxItem 再 t() 一次时会按 missing key 原样返回。
     *
     * @override
     * @protected
     * @returns {string}
     */
    _getLabel() {
        return this._formatParticipantsLabel();
    }
    /**
     * 与可见文案保持一致。
     *
     * @override
     * @protected
     * @returns {string}
     */
    _getTooltip() {
        return this._formatParticipantsLabel();
    }
    /**
     * 标签已在 _getLabel 完成插值，不再向 ToolboxItem 传 labelProps。
     *
     * @override
     * @protected
     * @returns {Object}
     */
    _getLabelProps() {
        // 返回空对象会被当成 truthy，ToolboxItem 会再 t(已插值文案, {})，仍安全；
        // 但显式空对象比 undefined 更符合 AbstractButton.labelProps: Object 的约定。
        return {};
    }
    /**
     * Override the _getAccessibilityLabel method to incorporate the dynamic participant count.
     *
     * @override
     * @returns {string}
     */
    _getAccessibilityLabel() {
        const { t, _participantsCount, _isOpen } = this.props;
        if (_isOpen) {
            return t('toolbar.accessibilityLabel.closeParticipantsPane');
        }
        return t('toolbar.accessibilityLabel.participants', {
            participantsCount: this._safeParticipantCount(_participantsCount)
        });
    }
    /**
     * 禁止追加角标等额外节点。
     *
     * @override
     * @protected
     * @returns {null}
     */
    _getElementAfter() {
        return null;
    }
    /**
     * Overrides AbstractButton's {@link Component#render()}.
     *
     * @override
     * @protected
     * @returns {React$Node}
     */
    render() {
        const { _isParticipantsPaneEnabled } = this.props;
        if (!_isParticipantsPaneEnabled) {
            return null;
        }
        return super.render();
    }
    /**
     * 生成底栏/tooltip 用的成员文案。
     *
     * @private
     * @returns {string}
     */
    _formatParticipantsLabel() {
        const { t, _isMobileToolbar, _participantsCount } = this.props;
        const key = _isMobileToolbar ? 'toolbar.manageParticipants' : 'toolbar.participants';
        return t(key, {
            participantCount: this._safeParticipantCount(_participantsCount)
        });
    }
    /**
     * @param {number|undefined} value - 原始人数.
     * @returns {number}
     */
    _safeParticipantCount(value) {
        return typeof value === 'number' && !Number.isNaN(value) ? value : 0;
    }
}
/**
 * Maps part of the Redux state to the props of this component.
 *
 * @param {Object} state - The Redux state.
 * @returns {IProps}
 */
function mapStateToProps(state) {
    const { isOpen } = state['features/participants-pane'];
    const { isNarrowLayout } = state['features/base/responsive-ui'];
    return {
        _isMobileToolbar: isMobileBrowser() || isNarrowLayout,
        _isOpen: isOpen,
        _isParticipantsPaneEnabled: isParticipantsPaneEnabled(state),
        _participantsCount: getParticipantCountForDisplay(state)
    };
}
export default translate(connect(mapStateToProps)(ParticipantsPaneButton));
